import { useEffect, useState } from 'react'
import firebase from '../../firebase/compat'

/**
 * Haalt één volledig gesprek met Decca op: de kop plus alle berichten.
 *
 * Zelfde reden om via een Cloud Function te lopen als useDeccaConversations:
 * `Conversations` is `isTeam()`-gated, dus de beheertenant leest de gesprekken
 * van andere organisaties niet in de browser.
 *
 * `conversation` blijft `null` zolang er geen antwoord is — onderscheiden van
 * 'gesprek bestaat niet', dat als fout terugkomt.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {string | undefined} conversationId - Conversations.ID (niet de document-id)
 * @returns {{conversation: object | null, messages: Array<object> | null, loading: boolean, error: string | null}}
 */
const useDeccaConversation = (conversationId) => {

    const [conversation, setConversation] = useState(/** @type {object | null} */ (null))
    const [messages, setMessages] = useState(/** @type {Array<object> | null} */ (null))
    const [loading, setLoading] = useState(Boolean(conversationId))
    const [error, setError] = useState(/** @type {string | null} */ (null))

    useEffect(() => {

        if (!conversationId) {
            setLoading(false)
            setError('Geen gesprek opgegeven.')
            return undefined
        }

        let afgebroken = false
        setLoading(true)
        setError(null)

        const fetch = async () => {
            try {
                // v2-functie in europe-west1 — regio expliciet.
                const callable = firebase.app().functions('europe-west1').httpsCallable('getDeccaConversation')
                const result = await callable({ conversationId })
                if (afgebroken) return
                setConversation(result.data?.conversation || null)
                setMessages(Array.isArray(result.data?.messages) ? result.data.messages : [])
            } catch (err) {
                if (afgebroken) return
                // eslint-disable-next-line no-console
                console.error('useDeccaConversation: getDeccaConversation failed', err)
                // Zelfde reden als in useDeccaConversations: 'internal' alleen
                // is geen melding waar een lezer iets aan heeft.
                setError(err.message
                    ? `Kon het gesprek niet ophalen (${err.message}).`
                    : 'Kon het gesprek niet ophalen.')
                setConversation(null)
                setMessages(null)
            } finally {
                if (!afgebroken) setLoading(false)
            }
        }

        fetch()
        return () => { afgebroken = true }
    }, [conversationId])

    return { conversation, messages, loading, error }
}

export default useDeccaConversation
